<template>
  <div>
    <v-card :elevation="hideShowOption ? 0 : 2">
      <v-card-title class="text-h6 section-title">
        {{ $t('Supporting_Links') }} {{ links.length ? `(${links.length})` : '' }}
        <template v-if="canShowShowButton">
          <v-tooltip location="bottom" class="opacity-70">
            <template v-slot:activator="{ props }">
              <v-btn icon @click="show = !show" v-bind="props" flat>
                <v-icon>{{ show ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
              </v-btn>
            </template>
            <span>{{ show ? $t('Hide') : $t('Show') }}</span>
          </v-tooltip>
        </template>
        <v-tooltip location="bottom" class="opacity-70">
          <template v-slot:activator="{ props }" v-if="!disableAdd">
            <v-btn icon @click="showLinkAttachment = true" v-bind="props" elevation="0">
              <v-icon>mdi-plus-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('Attach_Link') }}</span>
        </v-tooltip>
        <v-spacer></v-spacer>
      </v-card-title>
      <v-card-text v-if="show">
        <div v-if="gettingLinks">{{ $t('Retrieving_Links') }}...</div>
        <template v-else>
          <v-alert v-if="links && links.length > 0" type="warning" dense icon="mdi-alert"  variant="outlined">
            <span style="font-size:16px"
              v-html="
                $t('Supporting_Links_Warning', {
                  processManagerEmail: this.processManagerEmail,
                  mocid: this.moc.mocId
                })
              "
            ></span>
          </v-alert>
          <v-container class="hidden-md-and-up">
            <v-row dense v-if="links.length">
              <v-col cols="12" sm="6" md="6" v-for="link in links" v-bind:key="link.id">
                <v-card>
                  <v-container>
                    <v-row dense>
                      <v-col v-if="canDelete">
                        <div>
                          <a href="#" class="noUnder" @click.prevent="setupDelete(link)">
                            <v-icon size="large" class="opacity-50">mdi-delete-forever-outline</v-icon>
                          </a>
                        </div>
                      </v-col>
                      <v-col>
                        <div>
                          <a v-bind:href="'//' + link.link" target="_blank">{{ link.link }}</a>
                        </div>
                        <div>
                          {{ link.title }}
                        </div>
                        <div>{{ (link.modifiedBy || {}).displayName }}</div>
                        <div>{{ $filters.DateTimeFilter(link.modifiedByDate) }}</div>
                      </v-col>
                      <v-col cols="1">
                        <div>
                          <a href="#" class="noUnder" @click.prevent="setupLinkDetails(link)">
                            <v-icon>mdi-dots-horizontal</v-icon>
                          </a>
                        </div>
                        <div>
                          <a href="#" class="noUnder" @click.prevent="setupEdit(link)">
                            <v-icon>mdi-pencil-outline</v-icon>
                          </a>
                        </div>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </v-col>
            </v-row>
            <v-row dense v-else>
              <v-col>
                {{ $t('No_Results') }}
              </v-col>
            </v-row>
          </v-container>
          <v-data-table class="hidden-sm-and-down table-layout-fixed-link" :headers="dataTableHeaders" :items="links">
            <template v-slot:item="props">
              <tr>
                <td>
                  <a v-if="canDelete" href="#" class="noUnder" @click.prevent="setupDelete(props.item)">
                    <v-icon size="large" class="opacity-50">mdi-delete-forever-outline</v-icon>
                  </a>
                </td>
                <td>
                  <a v-bind:href="'//' + props.item.link" target="_blank">{{ props.item.link }}</a>
                </td>
                <td>
                  {{ props.item.title }}
                </td>
                <td>{{ (props.item.modifiedBy || {}).displayName }}</td>
                <td>{{ $filters.DateTimeFilter(props.item.modifiedByDate) }}</td>
                <td>
                  <a href="#" class="noUnder" @click.prevent="setupLinkDetails(props.item)">
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </a>
                </td>
              </tr>
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>

    <toaster ref="toaster"></toaster>
    <v-dialog v-model="showDeleteConfirmation" persistent max-width="350">
      <v-card>
        <v-toolbar color="warning" dense><v-spacer></v-spacer><v-icon class="me-3" color="black">mdi-bell-alert-outline</v-icon></v-toolbar>
        <v-card-text class="text-h6 font-weight-bold">{{ $t('Confirm_DeleteLink') }}</v-card-text>
        <v-card-text>
          {{ $t('Notification_DeletingLink') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="info" text class="card-button" @click="cancelDelete">{{ $t('Cancel') }}</v-btn>
          <v-btn color="error" text class="card-button" @click="confirmDelete">{{ $t('Delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showLinkDetails" max-width="500">
      <v-card v-if="showLinkDetails">
        <v-toolbar color="blue-grey" dense></v-toolbar>
        <v-card-title>{{ detailsLink.title }}</v-card-title>
        <v-card-text class="font-color">
          <v-container>
            <v-row dense>
              <v-col>
                <div>{{ $t('Created_By') }}: {{ (detailsLink.insertedBy || {}).displayName }}</div>
                <div>{{ $t('Created_On') }}: {{ $filters.DateTimeFilter(detailsLink.insertedByDate) }}</div>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col>
                <div>{{ $t('Modified_By') }}: {{ (detailsLink.modifiedBy || {}).displayName }}</div>
                <div>{{ $t('Modified_On') }}: {{ $filters.DateTimeFilter(detailsLink.modifiedByDate) }}</div>
              </v-col>
            </v-row>
            <strong>{{ $t('Versions') }}</strong>
            <template v-for="(version, index) in reversedLinkVersions" :key="index">
              <v-row dense align="center">
                <v-col cols="1">
                  <a class="noUnder" target="_blank" v-bind:href="'//' + version.link">
                    <v-icon>link</v-icon>
                  </a>
                </v-col>
                <v-col>
                  <div>{{ $t('Created_By') }}: {{ (version.insertedBy || {}).displayName }}</div>
                  <div>{{ $t('Created_On') }}: {{ $filters.DateTimeFilter(version.insertedByDate) }}</div>
                  <div>
                    <pre style="white-space: pre-wrap;">{{ version.comment || '' }}</pre>
                  </div>
                </v-col>
              </v-row>
            </template>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn color="info" class="card-button" text @click="showLinkDetails = false">{{ $t('Close') }}</v-btn>
          <v-spacer></v-spacer> 
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showEditDialog" persistent max-width="500">
      <v-card>
        <v-toolbar color="blue-grey" dense></v-toolbar>
        <v-card-title>{{ $t('Edit_Link') }}</v-card-title>
        <v-card-text>
          <v-form ref="editForm" autocomplete="off">
            <v-container>
              <v-row dense>
                <v-col>
                  <v-text-field dense v-model="editLink" label="Link" :rules="[textRules.required]" ref="editLink"></v-text-field>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col>
                  <v-text-field
                    dense
                    v-model="editTitle"
                    label="Title"
                    :rules="[textRules.required]"
                    ref="editTitle"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col>
                  <v-textarea
                    dense
                    v-model="editComments"
                    label="Comments"
                    :rules="[textRules.required]"
                    ref="editComments"
                    rows="1"
                    auto-grow
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn color="info" text class="card-button" @click="clearEdit">{{ $t('Close') }}</v-btn>
          <v-btn color="info" text class="card-button" @click="submitEdit">{{ $t('Ok_Attach') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showLinkAttachment" persistent max-width="500">
      <v-card>
        <v-card-title>
          {{ $t('Create_Link') }}
        </v-card-title>
        <v-card-text>
          <v-container class="dense">
            <v-form ref="linkForm" autocomplete="off">
              <v-row dense>
                <v-col>
                  <v-text-field
                    dense
                    v-model="linkToAttach"
                    :label="$t('Prompt_PasteLink')"
                    variant="underlined"
                    :rules="[textRules.required]"
                    ref="link"
                  ></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field
                    dense
                    v-model="linkTitle"
                    :label="$t('Prompt_LinkName')"
                    variant="underlined"
                    :rules="[textRules.required]"
                    ref="linkTitle"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col>
                  <v-textarea
                    dense
                    v-model="linkComments"
                    :label="$t('Prompt_LinkComments')"
                    variant="underlined"
                    ref="linkComments"
                    rows="1"
                    auto-grow
                  ></v-textarea>
                </v-col>
              </v-row>
              <v-row>
                <v-btn @click.prevent="submitLink">{{ $t('OK') }}</v-btn>
                <v-btn @click.prevent="clearLink">{{ $t('Cancel') }}</v-btn>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
    <loading-dialog ref="loadingDialog"></loading-dialog>
  </div>
</template>

<script>
import MOCService from '@/services/moc.service'
import AdminService from '@/services/admin.service'
import Toaster from '@/components/Toaster'
import { FormStatus, Roles } from '@/common/'
import LoadingDialog from '@/components/mocForm/LoadingDialog'

export default {
  name: 'SupportingLinks',
  components: {
    Toaster,
    LoadingDialog
  },
  data() {
    return {
      show: this.hideShowOption,
      links: [],
      gettingLinks: false,
      linkToAttach: null,
      linkTitle: null,
      linkComments: null,
      showUpload: false,
      showLinkAttachment: false,
      showDeleteConfirmation: false,
      showEditDialog: false,
      textRules: {
        // required: [value => !!value || this.$t('Rule_Required')]
        required: value => !!value || this.$t('Rule_Required')
      },
      showLinkDetails: false,
      detailsLink: {},
      deleteLink: {},
      editLink: null,
      editTitle: null,
      editComments: null,
      editId: null,
      processManagerEmail: null,
      dataTableHeaders: [
        { title: '', key: '', sortable: false, width: '5%' },
        { title: this.$t('Link'), title: 'link', width: '40%' },
        { title: this.$t('Title'), title: 'fileName' },
        { title: this.$t('Modified_By'), title: 'modifiedBy' },
        { title: this.$t('Modified_On'), title: 'modifiedByDate' },
        { title: '', title: '', sortable: false }
      ]
    }
  },
  props: {
    moc: {
      type: Object
    },
    hideShowOption: {
      type: Boolean,
      default: false
    },
    roles: {
      type: Array,
      default: null
    }
  },
  computed: {
    disableAdd: function () {
      return (
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDSTRANDED].value
      )
    },
    reversedLinkVersions() {
      if (!this.detailsLink.versions) return []
      return this.detailsLink.versions.slice().reverse()
    },
    canDelete() {
      // if any type of closed, you cannot remove
      if (
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDSTRANDED].value
      ) {
        return false
      }
      // now that we know it is not closed, are you one of the big 3?, if so, have at it.
      if (
        this.$store.state.currentUserAdminRoles.adminRoles.filter(
          role => role === 'ProcessManager' || role === 'ProcessManagerBackup' || role === 'CommunityOwner'
        ).length > 0
      ) {
        return true
      }
      // not a member of the big 3, sorry to hear that, lets find out where/if you can remove
      switch (this.moc.status.value) {
        case FormStatus.properties[FormStatus.NEW].value:
        case FormStatus.properties[FormStatus.DRAFT].value: {
          if (this.roles.includes(Roles.properties[Roles.INITIATOR].value) || this.roles.includes(Roles.COINITIATOR.value)) {
            return true
          }
          break
        }
        case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value: {
          if (this.moc.revisionHistory || this.moc.revisionHistory.length === 0) {
            if (this.roles.includes(Roles.properties[Roles.INITIATOR].value) || this.roles.includes(Roles.COINITIATOR.value)) {
              return true
            }
            break
          }
        }
      }
      return false
    },
    canShowShowButton: function() {
      return !this.hideShowOption && this.links.length > 0
    }
  },
  methods: {
    getLinks: function() {
      if (!this.moc.mocId) return
      if (!this.gettingLinks) {
        // get list of links for this moc
        this.gettingLinks = true
        return new Promise((resolve, reject) => {
          if (!this.moc.mocId) resolve()
          MOCService.getMocSupportingLinks(this.moc.mocId)
            .then(data => {
              this.links = data
              this.$emit('onLinksLoad', data)
              resolve()
            })
            .catch(error => {
              reject(error)
              console.error(this.$t('Error_GetLinks'), error)
            })
            .finally(() => {
              this.gettingLinks = false
            })
        })
      }
    },
    submitLink: async function() {
      const valid = await this.$refs.linkForm.validate()
      // console.log(`${JSON.stringify(valid)}`)
      
      // double check validations
      // if (!this.$refs.linkForm.validate()) {
      if (!valid.valid) {
        return
      }

      return new Promise((resolve, reject) => {
        this.$refs.loadingDialog.show()
        MOCService.uploadSupportingLink(
          this.moc.mocId,
          this.removeUrlPrefix(this.linkToAttach),
          this.linkComments,
          this.linkTitle
        )
          .then(async data => {
            await this.getLinks().finally(() => this.$refs.loadingDialog.hide())
            resolve()
          })
          .catch(error => {
            this.$refs.loadingDialog.error({ metadata: 'uploadSupportingLink', error, message: this.$t('Error_SaveLink') })
          })
          .finally(() => {
            this.clearLink()
          })
      })
    },
    removeUrlPrefix: function(url) {
      return url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '')
    },
    clearLink: function() {
      this.linkToAttach = null
      this.linkComments = null
      this.linkTitle = null
      this.showLinkAttachment = false
    },
    setupLinkDetails: function(link) {
      this.detailsLink = link
      this.showLinkDetails = true
    },
    setupDelete: function(link) {
      this.deleteLink = link
      this.showDeleteConfirmation = true
    },
    cancelDelete: function() {
      this.deleteLink = {}
      this.showDeleteConfirmation = false
    },
    confirmDelete: function() {
      return new Promise((resolve, reject) => {
        this.$refs.loadingDialog.show()
        MOCService.deleteSupportingLink(this.moc.mocId, this.deleteLink.id)
          .then(success => {
            this.deleteLink = {}
            this.showDeleteConfirmation = false
            this.$refs.loadingDialog.hide()
            resolve()
          })
          .catch(error => {
            this.$refs.loadingDialog.error({ metadata: 'deleteSupportingLink', error, message: this.$t('Error_DeleteLink') })
          })
          .finally(() => {
            this.getLinks()
          })
      })
    },
    setupEdit: function(link) {
      this.editLink = link.link
      this.editTitle = link.title
      this.editId = link.id
      this.showEditDialog = true
    },
    clearEdit: function() {
      this.editLink = null
      this.editTitle = null
      this.editId = null
      this.editComments = null
      this.showEditDialog = false
    },
    submitEdit: async function() {
      const valid = await this.$refs.linkForm.validate()
      // console.log(`${JSON.stringify(valid)}`)

      // double check validations
      // if (!this.$refs.editForm.validate()) {
      if (!valid.valid) {
        return
      }

      return new Promise((resolve, reject) => {
        this.$refs.loadingDialog.show()
        MOCService.uploadSupportingLink(this.moc.mocId, this.editLink, this.editComments, this.editTitle, this.editId)
          .then(async data => {
            await this.getLinks().finally(() => this.$refs.loadingDialog.hide())
            resolve()
          })
          .catch(error => {
            this.$refs.loadingDialog.error({ metadata: 'uploadSupportingLink', error, message: this.$t('Error_UpdateLink') })
          })
          .finally(() => {
            this.clearEdit()
          })
      })
    }
  },
  created() {
    this.getLinks()
  },
  mounted() {
    AdminService.getProcessManager().then(success => {
      this.processManagerEmail = success[0].processManagerPrimary.mail.toLowerCase()
    })
  }
}
</script>

<style scoped>
.noUnder {
  text-decoration: none;
}
.title {
  font-size: 0.95rem !important;
  font-weight: 400;
}
</style>
<style>
.table-layout-fixed-link table {
  table-layout: fixed;
}

.table-layout-fixed-link td {
  word-wrap: break-word;
}
</style>

<style>
.v-application a {
  color: #000000;
}
.card-button{
  box-shadow: none !important;
  background-color: transparent !important;
}
.font-color{
  color: rgba(0, 0, 0, .6);
}
.section-title{
  font-weight: 400 !important;
  font-size: 14px !important;
}
.card-button{
  box-shadow: none !important;
  background-color: transparent !important;
}
</style>